/**
 * Тайлангийн шүүлтүүр — URL-д (`?range=prev-month&store=…`).
 *
 * Хуудсыг сэргээхэд алдагдахгүй, холбоосоор хуваалцана. Анхдагч
 * «Энэ он» нь URL-д бичигдэхгүй — шүүлтүүргүй хуудас өмнөхтэйгөө ижил.
 *
 * ⚠️ `now`-ыг state-д барина. Рендер бүрд `new Date()` бол «Энэ он»-ы
 *    төгсгөл мс тутам өөрчлөгдөж query түлхүүр эргэлдэн хүсэлт
 *    зогсолтгүй явна. Шүүлтүүр солиход л шинэчлэгдэнэ.
 */

import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  isReportPreset, reportRange, ulaanbaatarDateKey,
  type ReportPreset, type ReportRange,
} from '@boots/domain';

export interface ReportFilter {
  preset: ReportPreset;
  storeId: string;
  /** `custom`-ийн `"YYYY-MM-DD"` огноо */
  from: string;
  to: string;
  /** `custom` огноо дутуу, буруу бол null */
  range: ReportRange | null;
  set: (patch: { preset?: ReportPreset; storeId?: string; from?: string; to?: string }) => void;
}

export function useReportFilter(): ReportFilter {
  const [params, setParams] = useSearchParams();
  const [now, setNow] = useState(() => new Date());

  const raw = params.get('range');
  const preset: ReportPreset = isReportPreset(raw) ? raw : 'year';
  const storeId = params.get('store') ?? '';
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';

  const set: ReportFilter['set'] = (patch) => {
    const next = new URLSearchParams(params);
    const put = (k: string, v: string | undefined, empty: string) => {
      if (v === undefined) return;
      if (v === empty) next.delete(k);
      else next.set(k, v);
    };

    put('range', patch.preset, 'year');
    put('store', patch.storeId, '');
    put('from', patch.from, '');
    put('to', patch.to, '');

    const p = patch.preset ?? preset;
    if (p !== 'custom') {
      next.delete('from');
      next.delete('to');
    } else if (patch.preset === 'custom' && !from && !to) {
      // Анх «Сонгох» дарахад энэ сарын 1-ээс өнөөдөр хүртэл бөглөнө
      const today = ulaanbaatarDateKey(new Date());
      next.set('from', `${today.slice(0, 8)}01`);
      next.set('to', today);
    }

    setNow(new Date());
    setParams(next, { replace: true });
  };

  return {
    preset, storeId, from, to, set,
    range: reportRange(preset, now, preset === 'custom' ? { from, to } : undefined),
  };
}

/**
 * `/reports/sales` ба `/reports/sales/export`-ийн шүүлт. Муж нь `[from, to)`,
 * API нь `lte` тул 1ms хасна — эс тэгвээс дараагийн мужийн 00:00:00.000-ийн
 * борлуулалт хоёр газар орно.
 */
export function salesQuery(from: Date, to: Date, storeId: string): string {
  const q = new URLSearchParams({
    from: from.toISOString(),
    to: new Date(to.getTime() - 1).toISOString(),
  });
  if (storeId) q.set('storeId', storeId);
  return q.toString();
}
